import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ClipboardList, Download, ExternalLink, Loader2, MessageSquare, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  ASSIGNED_TASKS_ASSIGN_ALL,
  ASSIGNED_TASKS_FILTER_ALL,
  ASSIGNED_TASKS_FILTER_AWAITING,
  buildAssignedTasksFilterChips,
  groupOpenTasksByAuxiliary,
  splitOpenTasksByStatus,
} from "@/lib/assignedTasksGrouping";
import {
  DEFAULT_ASSIGNED_TASK_DUE_TIME,
  composeAssignedTaskDue,
  formatAssignedTaskDueLabel,
} from "@/lib/assignedTaskDue";
import {
  buildAssignedTasksCsvRows,
  downloadAssignedTasksCsv,
  filterTasksByClosedMonth,
} from "@/lib/assignedTasksReport";
import { canManageWorkspaceTeam } from "@/lib/auxiliaryRoleGuide";
import { isWorkspaceAuxiliaryMember, workspaceUserDisplayName } from "@/modules/despacho/despachoWorkspaceAuxiliaries";
import { PANEL_GENERAL_EQUIPO_TAB } from "@/modules/panelGeneralTabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { toast } from "sonner";

function formatTaskDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function priorityTone(priority) {
  if (priority === "alta") return "border-rose-200 bg-rose-50/80";
  if (priority === "baja") return "border-slate-200 bg-slate-50";
  return "border-amber-200 bg-amber-50/60";
}

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Tareas asignadas admin → auxiliar. Misma API en BNT Asistente (rápido) y Actividades de Equipo (control/reportes).
 * @param {"assistant"|"equipo"} variant
 */
export default function AssignedTasksPanel({ user, onTasksChanged, variant = "assistant" }) {
  const isEquipoHub = variant === "equipo";
  const canAssign = canManageWorkspaceTeam(user);
  const [tasks, setTasks] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assigneeId, setAssigneeId] = useState("");
  const [body, setBody] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState(DEFAULT_ASSIGNED_TASK_DUE_TIME);
  const [listScope, setListScope] = useState(isEquipoHub ? "open" : "open");
  const [filterAssigneeId, setFilterAssigneeId] = useState("");
  const [reportMonth, setReportMonth] = useState(currentMonthValue());
  const [assignFormOpen, setAssignFormOpen] = useState(isEquipoHub);
  const [assistantListFilter, setAssistantListFilter] = useState(ASSIGNED_TASKS_FILTER_ALL);
  const [expandedAuxId, setExpandedAuxId] = useState("");
  const [threadTaskId, setThreadTaskId] = useState("");
  const [threadItems, setThreadItems] = useState([]);
  const [threadLocked, setThreadLocked] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadDraft, setThreadDraft] = useState("");
  const [threadSaving, setThreadSaving] = useState(false);

  const auxiliaries = useMemo(
    () => (teamUsers || []).filter(isWorkspaceAuxiliaryMember),
    [teamUsers],
  );

  const usersById = useMemo(() => {
    const map = new Map();
    for (const row of teamUsers || []) {
      if (row?.id) map.set(row.id, row);
    }
    return map;
  }, [teamUsers]);

  const apiScope = isEquipoHub ? listScope : "open";

  const reload = useCallback(async ({ notify = false } = {}) => {
    setLoading(true);
    try {
      const requests = [
        api.get("/asistente/assigned-tasks", { params: { scope: apiScope }, timeout: 20000 }),
      ];
      if (canAssign || isEquipoHub) {
        requests.push(api.get("/admin/users", { timeout: 20000 }).catch(() => ({ data: [] })));
      }
      const [tasksRes, usersRes] = await Promise.all(requests);
      setTasks(Array.isArray(tasksRes.data?.items) ? tasksRes.data.items : []);
      if (canAssign || isEquipoHub) {
        setTeamUsers(Array.isArray(usersRes?.data) ? usersRes.data : []);
      }
      if (notify) onTasksChanged?.();
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudieron cargar las tareas");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [apiScope, canAssign, isEquipoHub, onTasksChanged]);

  useEffect(() => {
    reload({ notify: false });
  }, [reload]);

  const createTask = async (e) => {
    e?.preventDefault?.();
    if (!canAssign) return;
    const text = body.trim();
    if (!assigneeId) {
      toast.error("Seleccione un auxiliar");
      return;
    }
    if (!text) {
      toast.error("Escriba la tarea");
      return;
    }
    const targetIds = assigneeId === ASSIGNED_TASKS_ASSIGN_ALL
      ? auxiliaries.map((row) => row.id).filter(Boolean)
      : [assigneeId];
    if (!targetIds.length) {
      toast.error("No hay auxiliares activos para asignar");
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        targetIds.map((assigned_to_user_id) => api.post("/asistente/assigned-tasks", {
          assigned_to_user_id,
          body: text,
          due_date: composeAssignedTaskDue(dueDate, dueTime),
        })),
      );
      setBody("");
      setDueDate("");
      setDueTime(DEFAULT_ASSIGNED_TASK_DUE_TIME);
      setAssigneeId("");
      if (!isEquipoHub) setAssignFormOpen(false);
      toast.success(
        targetIds.length > 1
          ? `Tarea asignada a ${targetIds.length} auxiliares`
          : "Tarea asignada",
      );
      await reload({ notify: true });
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo asignar la tarea");
    } finally {
      setSaving(false);
    }
  };

  const patchTask = async (taskId, payload) => {
    try {
      await api.patch(`/asistente/assigned-tasks/${taskId}`, payload);
      await reload({ notify: true });
      if (payload?.aux_read != null || payload?.aux_completed != null) {
        window.dispatchEvent(new CustomEvent("visor:assigned-tasks-changed"));
      }
      if (threadTaskId === taskId) {
        await loadThread(taskId, { silent: true });
      }
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo actualizar la tarea");
    }
  };

  const loadThread = useCallback(async (taskId, { silent = false } = {}) => {
    if (!taskId) return;
    if (!silent) setThreadLoading(true);
    try {
      const { data } = await api.get(`/asistente/assigned-tasks/${taskId}/messages`, { timeout: 20000 });
      setThreadItems(Array.isArray(data?.items) ? data.items : []);
      setThreadLocked(Boolean(data?.thread_locked));
    } catch (err) {
      if (!silent) toast.error(formatApiError(err) || "No se pudo cargar el seguimiento");
      setThreadItems([]);
    } finally {
      if (!silent) setThreadLoading(false);
    }
  }, []);

  const openThread = async (taskId) => {
    if (threadTaskId === taskId) {
      setThreadTaskId("");
      setThreadItems([]);
      setThreadDraft("");
      return;
    }
    setThreadTaskId(taskId);
    setThreadDraft("");
    await loadThread(taskId);
  };

  const sendThreadMessage = async (taskId) => {
    const text = threadDraft.trim();
    if (!text) {
      toast.error("Escriba la observación");
      return;
    }
    setThreadSaving(true);
    try {
      await api.post(`/asistente/assigned-tasks/${taskId}/messages`, { body: text });
      setThreadDraft("");
      toast.success("Observación registrada");
      await loadThread(taskId, { silent: true });
      await reload({ notify: true });
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo guardar la observación");
    } finally {
      setThreadSaving(false);
    }
  };

  const purgeThread = async (taskId) => {
    if (!canAssign) return;
    if (!window.confirm("¿Borrar permanentemente todo el historial de observaciones de esta tarea?")) return;
    try {
      await api.delete(`/asistente/assigned-tasks/${taskId}/messages`);
      toast.success("Historial borrado");
      await loadThread(taskId, { silent: true });
      await reload({ notify: true });
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo borrar el historial");
    }
  };

  const deleteTaskPermanent = async (taskId) => {
    if (!canAssign) return;
    if (!window.confirm("¿Borrar permanentemente esta tarea y su historial? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/asistente/assigned-tasks/${taskId}`);
      toast.success("Tarea eliminada");
      if (threadTaskId === taskId) {
        setThreadTaskId("");
        setThreadItems([]);
        setThreadDraft("");
      }
      await reload({ notify: true });
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo eliminar la tarea");
    }
  };

  const filteredTasks = useMemo(() => {
    let rows = tasks;
    if (filterAssigneeId) {
      rows = rows.filter((t) => t.assigned_to_user_id === filterAssigneeId);
    }
    return rows;
  }, [tasks, filterAssigneeId]);

  const openTasks = filteredTasks.filter((t) => !t.admin_closed_at);
  const closedTasks = filteredTasks.filter((t) => t.admin_closed_at);
  const { awaiting: awaitingTasks } = splitOpenTasksByStatus(openTasks);
  const awaitingAdmin = awaitingTasks.length;

  const assistantGroups = useMemo(
    () => groupOpenTasksByAuxiliary(openTasks, auxiliaries, workspaceUserDisplayName),
    [openTasks, auxiliaries],
  );

  const assistantFilterChips = useMemo(
    () => buildAssignedTasksFilterChips(openTasks, auxiliaries, workspaceUserDisplayName),
    [openTasks, auxiliaries],
  );

  const assistantGroupsToShow = useMemo(() => {
    if (assistantListFilter === ASSIGNED_TASKS_FILTER_ALL) {
      return assistantGroups.filter((group) => group.openCount > 0);
    }
    if (assistantListFilter === ASSIGNED_TASKS_FILTER_AWAITING) return [];
    return assistantGroups.filter((group) => group.id === assistantListFilter);
  }, [assistantGroups, assistantListFilter]);

  useEffect(() => {
    if (
      assistantListFilter
      && assistantListFilter !== ASSIGNED_TASKS_FILTER_ALL
      && assistantListFilter !== ASSIGNED_TASKS_FILTER_AWAITING
    ) {
      setExpandedAuxId(assistantListFilter);
    }
  }, [assistantListFilter]);

  const handleAssistantFilterChip = (chipId) => {
    setAssistantListFilter(chipId);
    if (chipId !== ASSIGNED_TASKS_FILTER_ALL && chipId !== ASSIGNED_TASKS_FILTER_AWAITING) {
      setExpandedAuxId(chipId);
    }
  };

  const renderAssigneeSelect = (compact = false) => (
    <div>
      <Label className={compact ? "text-[10px] text-slate-600" : "text-xs text-slate-600"}>Auxiliar</Label>
      <select
        className={`${compact ? "mt-0.5 text-xs py-1.5" : "mt-1 text-sm py-2"} w-full rounded-md border border-input bg-background px-2`}
        value={assigneeId}
        onChange={(e) => setAssigneeId(e.target.value)}
        data-testid={`${testIdPrefix}-assignee`}
      >
        <option value="">Seleccione…</option>
        {auxiliaries.length > 1 ? (
          <option value={ASSIGNED_TASKS_ASSIGN_ALL}>
            Todos los auxiliares ({auxiliaries.length})
          </option>
        ) : null}
        {auxiliaries.map((row) => (
          <option key={row.id} value={row.id}>
            {workspaceUserDisplayName(row)}
          </option>
        ))}
      </select>
    </div>
  );

  const exportMonthlyReport = async () => {
    if (!canAssign) return;
    try {
      const { data } = await api.get("/asistente/assigned-tasks", { params: { scope: "closed" }, timeout: 30000 });
      const allClosed = Array.isArray(data?.items) ? data.items : [];
      const monthRows = filterTasksByClosedMonth(allClosed, reportMonth);
      if (!monthRows.length) {
        toast.message(`Sin tareas concluidas en ${reportMonth}`);
        return;
      }
      const csv = buildAssignedTasksCsvRows(monthRows, usersById);
      downloadAssignedTasksCsv(csv, `tareas-equipo-${reportMonth}.csv`);
      toast.success(`Reporte ${reportMonth} descargado`);
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo generar el reporte");
    }
  };

  const testIdPrefix = isEquipoHub ? "equipo-assigned-tasks" : "bnt-asistente-mis-tareas";

  const renderTaskRow = (task, { showAssignee = true, closed = false } = {}) => {
    const assignee = usersById.get(task.assigned_to_user_id);
    const assigneeName = assignee ? workspaceUserDisplayName(assignee) : "Auxiliar";
    const auxDone = Boolean(task.aux_completed_at);
    const auxRead = Boolean(task.aux_read_at);
    const threadOpen = threadTaskId === task.id;
    const msgCount = Number(task.messages_count || 0);
    const canToggleAuxFlags = canAssign || task.assigned_to_user_id === user?.id;
    return (
      <li
        key={task.id}
        className={`rounded-md border px-3 py-2 text-xs ${
          closed ? "border-slate-200 bg-slate-50 text-slate-600" : priorityTone(task.priority)
        }`}
        data-testid={`${testIdPrefix}-row-${task.id}`}
      >
        {showAssignee ? (
          <p className={`text-[10px] font-semibold m-0 mb-1 ${closed ? "" : "text-[#0F2D5C]"}`}>{assigneeName}</p>
        ) : null}
        <p className="m-0 leading-relaxed whitespace-pre-wrap">{task.body}</p>
        {task.due_date ? (
          <p className="text-[10px] text-slate-500 m-0 mt-1">
            Límite: {formatAssignedTaskDueLabel(task.due_date)}
          </p>
        ) : null}
        {closed ? (
          <p className="text-[10px] m-0 mt-1">
            Cerrada: {formatTaskDate(task.admin_closed_at)}
            {task.aux_read_at ? ` · Leída: ${formatTaskDate(task.aux_read_at)}` : ""}
            {task.aux_completed_at ? ` · Auxiliar: ${formatTaskDate(task.aux_completed_at)}` : ""}
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-slate-200/80">
            <label className="inline-flex items-center gap-1.5 text-[10px] text-slate-700 cursor-pointer">
              <Checkbox
                checked={auxRead}
                onCheckedChange={(checked) => {
                  if (!checked && !canAssign) return;
                  patchTask(task.id, { aux_read: Boolean(checked) });
                }}
                disabled={!canToggleAuxFlags || (auxRead && !canAssign)}
                data-testid={`${testIdPrefix}-aux-read-${task.id}`}
              />
              Leído (auxiliar)
            </label>
            <label className="inline-flex items-center gap-1.5 text-[10px] text-slate-700 cursor-pointer">
              <Checkbox
                checked={auxDone}
                onCheckedChange={(checked) => {
                  if (!checked && !canAssign) return;
                  patchTask(task.id, { aux_completed: Boolean(checked) });
                }}
                disabled={!canToggleAuxFlags}
                data-testid={`${testIdPrefix}-aux-done-${task.id}`}
              />
              Realizada (auxiliar)
            </label>
            {canAssign ? (
              <label className="inline-flex items-center gap-1.5 text-[10px] text-slate-700 cursor-pointer">
                <Checkbox
                  checked={false}
                  onCheckedChange={() => patchTask(task.id, { admin_closed: true })}
                  data-testid={`${testIdPrefix}-admin-close-${task.id}`}
                />
                Concluida (admin)
              </label>
            ) : auxDone ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-800">
                <Check className="w-3 h-3" aria-hidden />
                Esperando validación del administrador
              </span>
            ) : auxRead ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-sky-800">
                Leída · pendiente de realizar
              </span>
            ) : null}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[10px] font-medium text-[#0F2D5C] underline-offset-2 hover:underline"
            onClick={() => openThread(task.id)}
            data-testid={`${testIdPrefix}-thread-toggle-${task.id}`}
          >
            <MessageSquare className="w-3 h-3" aria-hidden />
            Seguimiento{msgCount > 0 ? ` (${msgCount})` : ""}
          </button>
          {canAssign ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[10px] text-rose-700 hover:underline"
              onClick={() => deleteTaskPermanent(task.id)}
              data-testid={`${testIdPrefix}-delete-task-${task.id}`}
            >
              <Trash2 className="w-3 h-3" aria-hidden />
              Borrar tarea
            </button>
          ) : null}
        </div>
        {threadOpen ? (
          <div
            className="mt-2 rounded border border-slate-200 bg-white/90 p-2 space-y-2"
            data-testid={`${testIdPrefix}-thread-${task.id}`}
          >
            {threadLoading ? (
              <p className="text-[10px] text-slate-500 m-0">
                <Loader2 className="w-3 h-3 animate-spin inline-block mr-1" aria-hidden />
                Cargando historial…
              </p>
            ) : null}
            {!threadLoading && threadItems.length === 0 ? (
              <p className="text-[10px] text-slate-500 m-0">
                {threadLocked
                  ? "Sin observaciones en el historial."
                  : "Sin observaciones aún. Escriba una contestación o avance."}
              </p>
            ) : null}
            {!threadLoading && threadItems.length > 0 ? (
              <ul className="m-0 p-0 list-none space-y-1.5 max-h-48 overflow-y-auto">
                {threadItems.map((msg) => (
                  <li
                    key={msg.id}
                    className="rounded border border-slate-100 bg-slate-50 px-2 py-1.5"
                    data-testid={`${testIdPrefix}-thread-msg-${msg.id}`}
                  >
                    <p className="text-[9px] text-slate-500 m-0 mb-0.5">
                      {msg.author_name || (msg.author_role === "admin" ? "Administrador" : "Auxiliar")}
                      {msg.created_at ? ` · ${formatTaskDate(msg.created_at)}` : ""}
                    </p>
                    <p className="m-0 text-[11px] whitespace-pre-wrap leading-relaxed">{msg.body}</p>
                  </li>
                ))}
              </ul>
            ) : null}
            {threadLocked ? (
              <p className="text-[10px] text-slate-500 m-0">
                Tarea concluida: el historial queda congelado.
                {canAssign ? " Reabra la tarea para añadir observaciones." : ""}
              </p>
            ) : (
              <div className="space-y-1.5">
                <SpellcheckTextarea
                  value={threadDraft}
                  onChange={(e) => setThreadDraft(e.target.value)}
                  rows={2}
                  className="text-xs min-h-[3.5rem]"
                  placeholder="Observación o contestación…"
                  testId={`${testIdPrefix}-thread-draft-${task.id}`}
                  enableSpellcheck
                />
                <Button
                  type="button"
                  size="sm"
                  className="h-7 text-[10px] bg-[#0F2D5C] hover:bg-[#0a2248]"
                  disabled={threadSaving || !threadDraft.trim()}
                  onClick={() => sendThreadMessage(task.id)}
                  data-testid={`${testIdPrefix}-thread-send-${task.id}`}
                >
                  {threadSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Enviar observación"}
                </Button>
              </div>
            )}
            {canAssign && threadItems.length > 0 ? (
              <button
                type="button"
                className="text-[10px] text-rose-700 hover:underline"
                onClick={() => purgeThread(task.id)}
                data-testid={`${testIdPrefix}-thread-purge-${task.id}`}
              >
                Borrar historial permanentemente
              </button>
            ) : null}
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <div className="space-y-3" data-testid={isEquipoHub ? "equipo-assigned-tasks-panel" : "bnt-asistente-mis-tareas-panel"}>
      {isEquipoHub || !canAssign ? (
        <div className="rounded-md border border-[#0F2D5C]/15 bg-[#0F2D5C]/[0.04] px-3 py-2 text-[10px] text-slate-700">
          {isEquipoHub ? (
            <p className="m-0">
              <strong>Control y reportes</strong> — mismas tareas que en BNT Asistente, sincronizadas en tiempo real.
              El historial y los CSV mensuales viven aquí para no saturar el asistente.
            </p>
          ) : (
            <p className="m-0">
              <strong>Mis tareas del administrador</strong> — marque «Realizada» al terminar; el administrador validará en
              Actividades de Equipo.
            </p>
          )}
        </div>
      ) : null}

      {isEquipoHub && canAssign ? (
        <div
          className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white p-3 text-xs"
          data-testid="equipo-assigned-tasks-summary"
        >
          <div>
            <p className="text-[10px] text-slate-500 m-0">Abiertas</p>
            <p className="text-lg font-semibold text-[#0F2D5C] m-0">{openTasks.length}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 m-0">Por validar</p>
            <p className="text-lg font-semibold text-amber-800 m-0">{awaitingAdmin}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 m-0">Concluidas (vista)</p>
            <p className="text-lg font-semibold text-slate-600 m-0">{closedTasks.length}</p>
          </div>
        </div>
      ) : null}

      {canAssign && isEquipoHub ? (
        <form
          onSubmit={createTask}
          className="rounded-md border border-slate-200 bg-white p-4 space-y-3"
          data-testid={`${testIdPrefix}-assign-form`}
        >
          <p className="text-xs font-semibold text-[#0F2D5C] m-0 flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" aria-hidden />
            Asignar tarea (también disponible en BNT Asistente)
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {renderAssigneeSelect(false)}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-600">Fecha límite (opcional)</Label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDueDate(next);
                    if (next && !dueTime) setDueTime(DEFAULT_ASSIGNED_TASK_DUE_TIME);
                  }}
                  className="mt-1 h-9 text-sm"
                  data-testid={`${testIdPrefix}-due`}
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Hora (laboral)</Label>
                <Input
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  disabled={!dueDate}
                  className="mt-1 h-9 text-sm"
                  data-testid={`${testIdPrefix}-due-time`}
                />
              </div>
              <p className="col-span-2 text-[10px] text-slate-500 m-0">
                Horario laboral sugerido: 09:00–18:00 (fin de jornada por defecto).
              </p>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Tarea</Label>
            <SpellcheckTextarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              className="mt-1 text-sm min-h-[5rem]"
              placeholder="Ej. Tramitar cita IMSS, revisar papelería de captación…"
              testId={`${testIdPrefix}-body`}
              enableSpellcheck
            />
          </div>
          <Button
            type="submit"
            size="sm"
            className="h-9 text-sm bg-[#0F2D5C] hover:bg-[#0a2248]"
            disabled={saving || !assigneeId || !body.trim()}
            data-testid={`${testIdPrefix}-submit`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Asignar tarea"}
          </Button>
        </form>
      ) : null}

      {canAssign && !isEquipoHub ? (
        assignFormOpen ? (
          <form
            onSubmit={createTask}
            className="rounded-md border border-slate-200 bg-white p-3 space-y-2"
            data-testid={`${testIdPrefix}-assign-form`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-semibold text-[#0F2D5C] m-0 flex items-center gap-1">
                <Plus className="w-3 h-3" aria-hidden />
                Nueva tarea para auxiliar
              </p>
              <button
                type="button"
                className="text-[10px] text-slate-500 underline shrink-0"
                onClick={() => setAssignFormOpen(false)}
                data-testid={`${testIdPrefix}-assign-form-collapse`}
              >
                Ocultar
              </button>
            </div>
            {renderAssigneeSelect(true)}
            <div>
              <Label className="text-[10px] text-slate-600">Tarea</Label>
              <SpellcheckTextarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                className="mt-0.5 text-xs min-h-[4.5rem]"
                placeholder="Ej. Revisar papelería de empresa A…"
                testId={`${testIdPrefix}-body`}
                enableSpellcheck
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-slate-600">Fecha límite (opcional)</Label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDueDate(next);
                    if (next && !dueTime) setDueTime(DEFAULT_ASSIGNED_TASK_DUE_TIME);
                  }}
                  className="mt-0.5 h-8 text-xs"
                  data-testid={`${testIdPrefix}-due`}
                />
              </div>
              <div>
                <Label className="text-[10px] text-slate-600">Hora (laboral)</Label>
                <Input
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  disabled={!dueDate}
                  className="mt-0.5 h-8 text-xs"
                  data-testid={`${testIdPrefix}-due-time`}
                />
              </div>
              <p className="col-span-2 text-[10px] text-slate-500 m-0">
                Horario laboral sugerido: 09:00–18:00.
              </p>
            </div>
            <Button
              type="submit"
              size="sm"
              className="h-8 text-xs bg-[#0F2D5C] hover:bg-[#0a2248] w-full"
              disabled={saving || !assigneeId || !body.trim()}
              data-testid={`${testIdPrefix}-submit`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Asignar tarea"}
            </Button>
          </form>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-full text-xs border-[#0F2D5C]/30 text-[#0F2D5C]"
            onClick={() => setAssignFormOpen(true)}
            data-testid={`${testIdPrefix}-assign-form-toggle`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" aria-hidden />
            Asignar tarea
          </Button>
        )
      ) : null}

      {canAssign && !isEquipoHub && !loading && openTasks.length > 0 ? (
        <div
          className="flex gap-1 overflow-x-auto pb-0.5 -mx-0.5 px-0.5"
          data-testid={`${testIdPrefix}-filter-chips`}
        >
          {assistantFilterChips.map((chip) => {
            const active = assistantListFilter === chip.id;
            const tone = chip.id === ASSIGNED_TASKS_FILTER_AWAITING && chip.count > 0
              ? "border-amber-300 bg-amber-50 text-amber-950"
              : active
                ? "bg-[#0F2D5C] text-white border-[#0F2D5C]"
                : "bg-white text-slate-700 border-slate-200 hover:border-[#0F2D5C]/40";
            return (
              <button
                key={chip.id}
                type="button"
                className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-medium transition-colors ${tone}`}
                onClick={() => handleAssistantFilterChip(chip.id)}
                aria-pressed={active}
                data-testid={`${testIdPrefix}-filter-chip-${chip.id}`}
              >
                {chip.label}
                {chip.count > 0 ? ` (${chip.count})` : ""}
              </button>
            );
          })}
        </div>
      ) : null}

      {isEquipoHub && canAssign ? (
        <div className="flex flex-wrap items-end gap-3" data-testid="equipo-assigned-tasks-filters">
          <div>
            <Label className="text-[10px] text-slate-600">Vista</Label>
            <select
              className="mt-0.5 block rounded-md border border-input bg-background px-2 py-1.5 text-xs"
              value={listScope}
              onChange={(e) => setListScope(e.target.value)}
              data-testid="equipo-assigned-tasks-scope"
            >
              <option value="open">Abiertas</option>
              <option value="closed">Concluidas</option>
              <option value="all">Todas</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-slate-600">Auxiliar</Label>
            <select
              className="mt-0.5 block rounded-md border border-input bg-background px-2 py-1.5 text-xs"
              value={filterAssigneeId}
              onChange={(e) => setFilterAssigneeId(e.target.value)}
              data-testid="equipo-assigned-tasks-filter-assignee"
            >
              <option value="">Todos</option>
              {auxiliaries.map((row) => (
                <option key={row.id} value={row.id}>
                  {workspaceUserDisplayName(row)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-slate-600">Mes reporte CSV</Label>
            <Input
              type="month"
              value={reportMonth}
              onChange={(e) => setReportMonth(e.target.value)}
              className="mt-0.5 h-8 text-xs w-[9.5rem]"
              data-testid="equipo-assigned-tasks-report-month"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={exportMonthlyReport}
            data-testid="equipo-assigned-tasks-export-csv"
          >
            <Download className="w-3.5 h-3.5 mr-1" aria-hidden />
            Descargar CSV
          </Button>
        </div>
      ) : null}

      {loading ? (
        <p className="text-xs text-slate-500 text-center py-4">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" aria-hidden />
          Cargando tareas…
        </p>
      ) : null}

      {!loading && (isEquipoHub ? listScope !== "closed" : true) && openTasks.length === 0 ? (
        <p className="text-xs text-slate-500 m-0 rounded-md border border-dashed border-slate-200 px-3 py-4 text-center">
          {canAssign ? "Sin tareas abiertas." : "No tiene tareas pendientes del administrador."}
        </p>
      ) : null}

      {!loading && canAssign && !isEquipoHub && assistantListFilter === ASSIGNED_TASKS_FILTER_AWAITING && awaitingTasks.length > 0 ? (
        <div className="space-y-2" data-testid={`${testIdPrefix}-awaiting-list`}>
          <p className="text-[10px] font-semibold text-amber-900 m-0">Por validar (administrador)</p>
          <ul className="space-y-2 m-0 p-0 list-none">
            {awaitingTasks.map((task) => renderTaskRow(task, { showAssignee: true }))}
          </ul>
        </div>
      ) : null}

      {!loading && canAssign && !isEquipoHub && assistantListFilter === ASSIGNED_TASKS_FILTER_AWAITING && awaitingTasks.length === 0 ? (
        <p className="text-xs text-slate-500 m-0 text-center py-2">Sin tareas por validar.</p>
      ) : null}

      {!loading && canAssign && !isEquipoHub && assistantListFilter !== ASSIGNED_TASKS_FILTER_AWAITING && assistantGroupsToShow.length > 0 ? (
        <Accordion
          type="single"
          collapsible
          value={expandedAuxId}
          onValueChange={setExpandedAuxId}
          className="rounded-md border border-slate-200 bg-white px-2"
          data-testid={`${testIdPrefix}-aux-accordion`}
        >
          {assistantGroupsToShow.map((group) => (
            <AccordionItem key={group.id} value={group.id} className="border-slate-100">
              <AccordionTrigger className="py-2 text-[11px] hover:no-underline [&[data-state=open]>svg]:rotate-180">
                <span className="flex flex-1 items-center justify-between gap-2 pr-2 text-left">
                  <span className="font-semibold text-[#0F2D5C] truncate">{group.name}</span>
                  <span className="text-[10px] text-slate-500 shrink-0">
                    {group.pending.length > 0 ? `${group.pending.length} pend.` : null}
                    {group.pending.length > 0 && group.awaiting.length > 0 ? " · " : null}
                    {group.awaiting.length > 0 ? (
                      <span className="text-amber-800">{group.awaiting.length} validar</span>
                    ) : null}
                    {group.openCount === 0 ? "0 abiertas" : null}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-2 pt-0">
                <ul className="space-y-2 m-0 p-0 list-none">
                  {[...group.pending, ...group.awaiting].map((task) => renderTaskRow(task, { showAssignee: false }))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : null}

      {!loading && canAssign && !isEquipoHub && assistantListFilter !== ASSIGNED_TASKS_FILTER_AWAITING && assistantListFilter !== ASSIGNED_TASKS_FILTER_ALL && assistantGroupsToShow.length === 0 ? (
        <p className="text-xs text-slate-500 m-0 text-center py-2">Sin tareas abiertas para este auxiliar.</p>
      ) : null}

      {!loading && (isEquipoHub || !canAssign) && (isEquipoHub ? listScope !== "closed" : true) && openTasks.length > 0 ? (
        <ul className="space-y-2 m-0 p-0 list-none" data-testid={`${testIdPrefix}-open-list`}>
          {openTasks.map((task) => renderTaskRow(task, { showAssignee: canAssign || isEquipoHub }))}
        </ul>
      ) : null}

      {!loading && isEquipoHub && listScope !== "open" && closedTasks.length > 0 ? (
        <ul className="space-y-2 m-0 p-0 list-none" data-testid={`${testIdPrefix}-closed-list`}>
          {closedTasks.map((task) => renderTaskRow(task, { showAssignee: true, closed: true }))}
        </ul>
      ) : null}

      {!loading && isEquipoHub && listScope === "closed" && closedTasks.length === 0 ? (
        <p className="text-xs text-slate-500 m-0">Sin tareas concluidas en esta vista.</p>
      ) : null}

      {!isEquipoHub && canAssign ? (
        <p className="text-[10px] text-slate-500 m-0 flex items-center gap-1">
          <ExternalLink className="w-3 h-3 shrink-0" aria-hidden />
          <Link to={PANEL_GENERAL_EQUIPO_TAB} className="underline text-[#0F2D5C]">
            Actividades de Equipo
          </Link>
          — historial, filtros y reportes CSV
        </p>
      ) : null}

      {!canAssign && !loading && !isEquipoHub && openTasks.length > 0 ? (
        <p className="text-[10px] text-slate-400 m-0 flex items-center gap-1">
          <ClipboardList className="w-3 h-3" aria-hidden />
          Alertas de clientes en la pestaña Alertas del asistente.
        </p>
      ) : null}
    </div>
  );
}
